import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';

/**
 * This hook can be used to perform input validation for
 * arbitrary form data.
 *
 * Example Setup:
 *
 *   import { useFormValidation, tcFormValidators } from '@techchange/components';
 *
 *   const { inputValidation, validateFormData } = useFormValidation({
 *       email: [tcFormValidators.required],
 *       email_confirmation: [
 *           tcFormValidators.required,
 *           tcFormValidators.matches('email'),
 *       ],
 *       password: [
 *           tcFormValidators.required,
 *           tcFormValidators.length(8, 64),
 *       ],
 *       password_confirmation: [
 *           tcFormValidators.requiredConditional((data) => Boolean(data.password))
 *       ],
 *       custom_json_field: {
 *           subfield_one: [tcFormValidators.required],
 *           subfield_two: [tcFormValidators.required],
 *           subfield_three [tcFormValidators.required],
 *       },
 *       custom_array_field: [tcFormValidators.arrayContainingObjects('id', {
 *           title: [tcFormValidators.required]
 *       })],
 *   });
 *
 * Example Usage (on single form field):
 *
 *   validateFormData({ password: 'mynewpassword' });
 *
 * Example Usage (on all form fields):
 *
 *   validateFormData(data);
 *
 * @param {object} options  The options.
 *
 * @returns {object} result
 *
 * @returns {object} result.inputValidation  The current state of validation for the form data.
 *
 * @returns {func} result.setInputValidation  Callback function to change the state of
 *                                            validation for the form data.
 *
 * @returns {func} result.validateFormData  Callback function to run validation on the form data.
 */
const useFormValidation = (options = {}) => {

	const intl = useIntl();

	const [validationOptions] = useState(options);

	const [inputValidation, setInputValidation] = useState(
		() => initializeInputValidation(options)
	);

	// Reset the input validation state
	const resetInputValidation = () => {

		setInputValidation(initializeInputValidation(options));

	};

	const validateFormData = useCallback((formData) => {

		const fieldNames = Object.keys(formData);

		const finalInputValidation = {};

		let isFormValid = true;

		fieldNames.forEach((name) => {

			const fieldRequirements = validationOptions[name];

			if (fieldRequirements) {

				// Check if field has a list of validation requirements
				if (Array.isArray(fieldRequirements)) {

					const fieldValue = formData[name];

					// Perform validation
					finalInputValidation[name] = validateField(
						fieldValue,
						fieldRequirements,
						formData,
						intl,
					);

					// Update overall form validation state
					isFormValid =
						isFormValid &&
						!finalInputValidation[name].invalid;

				// Check if field is JSON data and has nested list(s) of validation requirements
				} else if (typeof fieldRequirements === 'object') {

					// Update validation state for this JSON data field
					finalInputValidation[name] = {};

					const subFieldNames = Object.keys(fieldRequirements);

					// Loop through each nested field for a list of validation requirements
					subFieldNames.forEach((subName) => {

						const subFieldRequirements =
							validationOptions[name] &&
							validationOptions[name][subName];

						const subFieldValue =
							formData[name] &&
							formData[name][subName];

						// Perform validation
						finalInputValidation[name][subName] = validateField(
							subFieldValue,
							subFieldRequirements,
							formData,
							intl,
						);

						// Update overall form validation state
						isFormValid =
							isFormValid &&
							!finalInputValidation[name][subName].invalid;

					});

				}

			}

		});

		// Update the input validation state
		setInputValidation(finalInputValidation);

		// Check if the form data is valid (i.e. no errors)
		return isFormValid;

	}, [validationOptions, intl]);

	return {
		inputValidation,
		resetInputValidation,
		validateFormData,
	};

};

export default useFormValidation;

/**
 * Initialize the state of validation for the form data.
 *
 * @param {object} options  The options.
 *
 * @returns {object} result  The current state of validation for the form data.
 */
function initializeInputValidation(options) {

	const fieldNames = Object.keys(options);

	const result = {};

	fieldNames.forEach((name) => {

		if (Array.isArray(options[name])) {

			result[name] = {
				invalid: false,
				error: null,
			};

		// Support additional level of field validation for JSON data
		} else if (typeof options[name] === 'object') {

			const subFieldNames = Object.keys(options[name]);

			result[name] = {};

			subFieldNames.forEach((subName) => {

				result[name][subName] = {
					invalid: false,
					error: null,
				};

			});

		} else {

			console.warn(
				`The options.${name} type is invalid, must be one of ["array", "object"])`
			);

			result[name] = {
				invalid: false,
				error: null,
			};

		}

	});

	return result;

}

/**
 * Validates the state of a specific field for the form data.
 *
 * @param {any} fieldValue  The field value.
 *
 * @param {array} fieldRequirements  The list of validation requirements for the field.
 *
 * @param {object} parentData  The parent data, i.e. the form data.
 * NOTE: If using "arrayContainingObjects" validator, then it will be the nested object itself.
 *
 * @param {object} intl  The internationalization provider.
 *
 * @returns {object} result           The current state of validation for the field.
 * @returns {boolean} result.invalid  True, if field is invalid.
 * @returns {string} result.error     The error message (if any) for the field.
 */
export function validateField(fieldValue, fieldRequirements, parentData, intl) {

	let isValid = true, error = null, data = {};

	if (Array.isArray(fieldRequirements)) {

		// If so, loop through them one-by-one...
		for (let i = 0, len = fieldRequirements.length; i < len; i++) {

			// Run the validation method!
			const [isFieldValid, fieldError, fieldData] =
				fieldRequirements[i](fieldValue, parentData, intl);

			isValid = isValid && isFieldValid;
			error = fieldError;
			data = {
				...data,
				...fieldData,
			};

			// If any requirement fails, then the field is marked invalid
			if (!isValid) {
				break;
			}

		}

	}

	// Return the up-to-date input validation state for this field
	return {
		invalid: !isValid,
		error,
		data,
	};

}
